import { useState, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSupplierDuplicateCheck } from "@/hooks/useSupplierDuplicateCheck";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import StepIndicator from "./supplier-form/StepIndicator";
import CalendarStep from "./supplier-form/CalendarStep";
import ContactStep from "./supplier-form/ContactStep";
import CompanyStep from "./supplier-form/CompanyStep";
import SuccessMessage from "./supplier-form/SuccessMessage";
import { QuizPrefillData } from "./supplier-quiz/QuizLeadForm";

interface SupplierFormProps {
  isOpen: boolean;
  onClose: () => void;
  prefillData?: QuizPrefillData;
}

interface FormState {
  step: 1 | 2 | 3 | "success";
  scheduledDate: Date | null;
  scheduledTime: string | null;
  fullName: string;
  phone: string;
  email: string;
  cnpj: string;
  companyName: string;
  partnerCode: string;
}

const initialFormState: FormState = {
  step: 1,
  scheduledDate: null,
  scheduledTime: null,
  fullName: "",
  phone: "",
  email: "",
  cnpj: "",
  companyName: "",
  partnerCode: "",
};

const SupplierForm = ({ isOpen, onClose, prefillData }: SupplierFormProps) => {
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [loading, setLoading] = useState(false);
  const { duplicates, checkField } = useSupplierDuplicateCheck();
  const prospectIdRef = useRef<string | null>(null);

  // Apply prefill data when it changes and form opens
  useEffect(() => {
    if (prefillData && isOpen) {
      setFormState((prev) => ({
        ...prev,
        fullName: prefillData.name || prev.fullName,
        phone: prefillData.phone || prev.phone,
        email: prefillData.email || prev.email,
        cnpj: prefillData.cnpj || prev.cnpj,
      }));
    }
  }, [prefillData, isOpen]);

  const handleClose = () => {
    setFormState(initialFormState);
    prospectIdRef.current = null;
    onClose();
  };

  const handleSelectDate = (date: Date) => {
    setFormState((prev) => ({ ...prev, scheduledDate: date }));
  };

  const handleSelectTime = (time: string) => {
    setFormState((prev) => ({ ...prev, scheduledTime: time }));
  };

  // Upsert prospect data progressively
  const upsertProspect = async (data: Record<string, unknown>) => {
    try {
      // Capture partner code from typed field, URL or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("partner")) {
        localStorage.setItem("ameup_partner_code", urlParams.get("partner")!);
      }
      const partnerCode =
        (formState.partnerCode && formState.partnerCode.trim()) ||
        urlParams.get("partner") ||
        (typeof window !== "undefined" ? localStorage.getItem("ameup_partner_code") : null) ||
        undefined;

      if (prospectIdRef.current) {
        await supabase.functions.invoke("upsert-prospect", {
          body: { id: prospectIdRef.current, ...data, partnerCode },
        });
      } else {
        const { data: result } = await supabase.functions.invoke("upsert-prospect", {
          body: { ...data, partnerCode },
        });
        if (result?.prospectId) {
          prospectIdRef.current = result.prospectId;
        }
      }
    } catch (err) {
      console.error("Prospect upsert error:", err);
    }
  };

  const handleStep1Continue = async () => {
    setFormState((prev) => ({ ...prev, step: 2 }));
    // Save contact data to prospects
    await upsertProspect({
      fullName: formState.fullName,
      phone: formState.phone,
      source: "form",
      quizResponseId: prefillData?.quizResponseId || null,
    });
  };

  const handleStep2Continue = async () => {
    setFormState((prev) => ({ ...prev, step: 3 }));
    // Update prospect with company data
    await upsertProspect({
      fullName: formState.fullName,
      phone: formState.phone,
      email: formState.email,
      cnpj: formState.cnpj,
      companyName: formState.companyName,
      source: "form",
      quizResponseId: prefillData?.quizResponseId || null,
    });
  };

  const handleBack = (toStep: 1 | 2) => {
    setFormState((prev) => ({ ...prev, step: toStep }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const scheduledDate = formState.scheduledDate
        ? formState.scheduledDate.toISOString().split("T")[0]
        : "";

      // First update prospect with all final data including schedule
      if (prospectIdRef.current) {
        await upsertProspect({
          fullName: formState.fullName,
          phone: formState.phone,
          email: formState.email,
          cnpj: formState.cnpj,
          companyName: formState.companyName,
          scheduledDate,
          scheduledTime: formState.scheduledTime,
          source: "form",
          quizResponseId: prefillData?.quizResponseId || null,
        });
      }

      const { data, error } = await supabase.functions.invoke("register-supplier", {
        body: {
          fullName: formState.fullName,
          phone: formState.phone,
          email: formState.email,
          cnpj: formState.cnpj,
          companyName: formState.companyName,
          scheduledDate,
          scheduledTime: formState.scheduledTime,
          quizResponseId: prefillData?.quizResponseId || null,
          prospectId: prospectIdRef.current,
        },
      });

      // Extract message from edge function error response
      if (error) {
        let message = "Tente novamente mais tarde.";
        try {
          const context = JSON.parse(error.context?.body || "{}");
          if (context.error) message = context.error;
        } catch {
          if (error.message) message = error.message;
        }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Cadastro realizado com sucesso!",
        description: "Entraremos em contato para confirmar sua reunião.",
      });

      setFormState((prev) => ({ ...prev, step: "success" }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Tente novamente mais tarde.";
      toast({
        title: "Atenção",
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormState(initialFormState);
    prospectIdRef.current = null;
  };

  const currentStep = formState.step;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0 bg-background"
      >
        <SheetHeader className="px-6 pt-6 pb-2 shrink-0">
          <SheetTitle className="text-xl font-semibold text-foreground">
            {currentStep === "success"
              ? "Cadastro Concluído"
              : "Cadastro de Fornecedor"}
          </SheetTitle>
        </SheetHeader>

        {currentStep !== "success" && (
          <div className="px-6 shrink-0">
            <StepIndicator currentStep={currentStep} />
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 pb-8">
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <ContactStep
                key="step-1"
                fullName={formState.fullName}
                phone={formState.phone}
                onChangeFullName={(value) =>
                  setFormState((prev) => ({ ...prev, fullName: value }))
                }
                onChangePhone={(value) =>
                  setFormState((prev) => ({ ...prev, phone: value }))
                }
                onContinue={handleStep1Continue}
                phoneDuplicate={duplicates.phone}
                onCheckDuplicate={checkField}
              />
            )}

            {currentStep === 2 && (
              <CompanyStep
                key="step-2"
                email={formState.email}
                cnpj={formState.cnpj}
                companyName={formState.companyName}
                partnerCode={formState.partnerCode}
                onChangeEmail={(value) =>
                  setFormState((prev) => ({ ...prev, email: value }))
                }
                onChangeCnpj={(value) =>
                  setFormState((prev) => ({ ...prev, cnpj: value }))
                }
                onChangeCompanyName={(value) =>
                  setFormState((prev) => ({ ...prev, companyName: value }))
                }
                onChangePartnerCode={(value) =>
                  setFormState((prev) => ({ ...prev, partnerCode: value }))
                }
                onBack={() => handleBack(1)}
                onContinue={handleStep2Continue}
                emailDuplicate={duplicates.email}
                cnpjDuplicate={duplicates.cnpj}
                onCheckDuplicate={checkField}
              />
            )}

            {currentStep === 3 && (
              <CalendarStep
                key="step-3"
                selectedDate={formState.scheduledDate}
                selectedTime={formState.scheduledTime}
                onSelectDate={handleSelectDate}
                onSelectTime={handleSelectTime}
                onBack={() => handleBack(2)}
                onSubmit={handleSubmit}
                loading={loading}
              />
            )}

            {currentStep === "success" && (
              <SuccessMessage
                key="success"
                scheduledDate={formState.scheduledDate}
                scheduledTime={formState.scheduledTime}
                onReset={handleReset}
              />
            )}
          </AnimatePresence>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SupplierForm;
